import type { NavigationEntry } from "./navigation";

/**
 * Side navigation search (UX5): folds Arabic letter variants, diacritics and case so "فاتوره" finds "فاتورة" and
 * "اصول" finds "الأصول"; every word of the query must appear, in any order, in the entry's text in either language.
 * Level-3 screens (UX11) are searched like any other: their words count together with their groups' words.
 */
export function normalizeSearchText(value: string): string {
  return foldCharacters(value).replace(/\s+/gu, " ").trim();
}

/** Folds every character on its own, so a folded string can be mapped back to the original (see `matchRanges`). */
function foldCharacters(value: string): string {
  return value
    .toLocaleLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f\u064b-\u065f\u0670\u0640]/gu, "")
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/[^\p{L}\p{N}]+/gu, " ");
}

export function searchTerms(query: string): string[] {
  const normalized = normalizeSearchText(query);
  return normalized === "" ? [] : normalized.split(" ");
}

function matches(texts: readonly string[], terms: readonly string[]): boolean {
  const haystack = texts.map(normalizeSearchText).join(" ");
  return terms.every((term) => haystack.includes(term));
}

/**
 * Filters the navigation tree: a group whose own label matches keeps all its children; otherwise it keeps only the
 * matching children (and disappears when none match). A child's words count together with those of every group above
 * it ("فواتير مبيعات", "setup unit"). `labels` returns the entry's label in every language.
 */
export function filterNavigation(
  entries: readonly NavigationEntry[],
  query: string,
  labels: (entry: NavigationEntry) => readonly string[],
): NavigationEntry[] {
  const terms = searchTerms(query);
  return terms.length === 0 ? [...entries] : filterLevel(entries, terms, [], labels);
}

function filterLevel(
  entries: readonly NavigationEntry[],
  terms: readonly string[],
  context: readonly string[],
  labels: (entry: NavigationEntry) => readonly string[],
): NavigationEntry[] {
  return entries.flatMap((entry): NavigationEntry[] => {
    const own = [...labels(entry), ...context];
    if (matches(own, terms)) {
      return [entry];
    }
    if (!entry.children?.length) {
      return [];
    }
    const children = filterLevel(entry.children, terms, own, labels);
    return children.length > 0 ? [{ ...entry, children }] : [];
  });
}

/** First navigable path of the filtered tree, depth first (Enter in the search box opens it). */
export function firstPath(entries: readonly NavigationEntry[]): string | undefined {
  for (const entry of entries) {
    const path = entry.children?.length ? firstPath(entry.children) : entry.path;
    if (path) {
      return path;
    }
  }
  return undefined;
}

/**
 * Ranges `[start, end)` of `text` that match a search term, for highlighting. Matching uses the same folding as the
 * search, mapped back to the original characters (a diacritic stays inside its letter's range); overlapping ranges are
 * merged.
 */
export function matchRanges(text: string, terms: readonly string[]): [number, number][] {
  if (terms.length === 0) {
    return [];
  }
  // Folded text with, for every folded character, the index of the original character it came from.
  let folded = "";
  const origin: number[] = [];
  let index = 0;
  for (const character of text) {
    const part = foldCharacters(character);
    folded += part;
    origin.push(...Array.from(part, () => index));
    index += character.length;
  }
  const ranges: [number, number][] = [];
  for (const term of terms) {
    for (let at = folded.indexOf(term); at !== -1 && term !== ""; at = folded.indexOf(term, at + term.length)) {
      const start = origin[at] ?? 0;
      const lastOrigin = origin[at + term.length - 1] ?? start;
      // The range ends after the last matched character and any combining marks folded away behind it.
      let end = lastOrigin + 1;
      while (end < text.length && foldCharacters(text.charAt(end)) === "") end++;
      ranges.push([start, end]);
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const range of ranges) {
    const last = merged.at(-1);
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([range[0], range[1]]);
  }
  return merged;
}
